import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { Form } from "@circle-react/types/Form";
import { isEmpty } from "lodash";
import { infiniteQueryOptions, queryOptions } from "react-query";

export const formKey = createQueryKeyFactory("form");

export const FORMS_DEFAULT_PAGE_SIZE = 20;

export type FormsOrder = Partial<Record<"name" | "updated_at", "asc" | "desc">>;

interface ListParams {
  initialPage?: number;
  order?: FormsOrder;
  perPage?: number;
  query?: string;
  status?: string;
  /** Only forms attached to at least one newsletter list. */
  withNewsletterLists?: boolean;
}

export interface FormListPage {
  count: number;
  has_next_page: boolean;
  meta: FormStatusCounts;
  page: number;
  page_count: number;
  per_page: number;
  records: Form[];
}

export interface FormStatusCounts {
  all: number;
  draft: number;
  published: number;
}

export const formQueries = {
  list: ({
    perPage = FORMS_DEFAULT_PAGE_SIZE,
    initialPage = 1,
    order,
    query = "",
    status,
    withNewsletterLists = false,
  }: ListParams = {}) =>
    infiniteQueryOptions({
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      initialPageParam: initialPage,
      queryKey: formKey.key(
        "list",
        perPage,
        initialPage,
        query,
        status ?? "",
        withNewsletterLists,
        JSON.stringify(order ?? {}),
      ),
      queryFn: ({ pageParam = initialPage }: { pageParam?: number }) => {
        const filter = {
          ...(query && { query }),
          ...(status && { status }),
          ...(withNewsletterLists && { with_newsletter_lists: true }),
        };

        return reactQueryGet<FormListPage>(
          internalApi.forms.index({
            page: pageParam > 0 ? pageParam : 1,
            per_page: perPage,
            ...(!isEmpty(filter) && { filter }),
            ...(!isEmpty(order) && { order }),
          }),
        );
      },
    }),
  detail: (id: number) =>
    queryOptions({
      queryKey: formKey.key("detail", id),
      queryFn: () => reactQueryGet<Form>(internalApi.forms.show(id)),
    }),
};

export const formInvalidations = {
  all: () => queryClient.invalidateQueries({ queryKey: formKey.root() }),
};
